<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import VenuesAPI from 'dashboard/api/venues';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  venue: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const loading = ref(false);
const refreshing = ref(false);
const buzzData = ref(null);

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const score = computed(
  () => buzzData.value?.buzz_score ?? props.venue?.buzz_score
);
const avg28d = computed(
  () => buzzData.value?.buzz_score_avg_28d ?? props.venue?.buzz_score_avg_28d
);
const computedAt = computed(
  () => buzzData.value?.buzz_computed_at ?? props.venue?.buzz_computed_at
);
const summary = computed(() => buzzData.value?.summary || {});
const history = computed(() => buzzData.value?.history || []);

const categoryRows = computed(() => [
  {
    key: 'reviews',
    label: t('SETTINGS.SPACES.VENUES.BUZZ.CATEGORY_REVIEWS'),
    value: summary.value.b_reviews,
  },
  {
    key: 'media',
    label: t('SETTINGS.SPACES.VENUES.BUZZ.CATEGORY_MEDIA'),
    value: summary.value.b_media,
  },
  {
    key: 'footfall',
    label: t('SETTINGS.SPACES.VENUES.BUZZ.CATEGORY_FOOTFALL'),
    value: summary.value.b_footfall,
  },
]);

const fetchBuzz = async () => {
  if (!props.venue?.id) return;
  loading.value = true;
  try {
    const response = await VenuesAPI.getBuzz(props.venue.id);
    buzzData.value = response.data;
  } catch {
    buzzData.value = null;
  } finally {
    loading.value = false;
  }
};

const refreshBuzz = async () => {
  if (!props.venue?.id || refreshing.value) return;
  refreshing.value = true;
  try {
    await VenuesAPI.refreshBuzz(props.venue.id);
    useAlert(t('SETTINGS.SPACES.VENUES.BUZZ.REFRESH_QUEUED'));
    await fetchBuzz();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('SETTINGS.SPACES.VENUES.BUZZ.REFRESH_ERROR')
    );
  } finally {
    refreshing.value = false;
  }
};

onMounted(fetchBuzz);
watch(() => props.venue?.id, fetchBuzz);
</script>

<template>
  <div class="rounded-lg border border-n-weak p-4 space-y-4">
    <div class="flex items-start justify-between gap-2">
      <div>
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('SETTINGS.SPACES.VENUES.BUZZ.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ t('SETTINGS.SPACES.VENUES.BUZZ.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="isSuperAdmin"
        icon="i-lucide-refresh-cw"
        sm
        slate
        outlined
        :disabled="refreshing || loading"
        :is-loading="refreshing"
        :label="t('SETTINGS.SPACES.VENUES.BUZZ.REFRESH')"
        @click="refreshBuzz"
      />
    </div>

    <div v-if="loading" class="text-sm text-n-slate-10">
      {{ t('SETTINGS.SPACES.VENUES.BUZZ.LOADING') }}
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-3">
        <div class="rounded-lg bg-n-alpha-2 p-3">
          <p class="text-xs text-n-slate-10">
            {{ t('SETTINGS.SPACES.VENUES.BUZZ.SCORE') }}
          </p>
          <p
            class="text-5xl font-semibold tabular-nums text-n-slate-12 leading-none mb-0 inline-flex items-end gap-2"
          >
            {{ score ?? '—' }}
            <Icon icon="i-ph-hands-clapping" class="size-8 shrink-0 mb-0.5" />
          </p>
        </div>
        <div class="rounded-lg bg-n-alpha-2 p-3">
          <p class="text-xs text-n-slate-10">
            {{ t('SETTINGS.SPACES.VENUES.BUZZ.AVG_28D') }}
          </p>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ avg28d ?? '—' }}
          </p>
        </div>
      </div>

      <p v-if="computedAt" class="text-xs text-n-slate-10">
        {{ t('SETTINGS.SPACES.VENUES.BUZZ.LAST_UPDATED') }}
        {{ new Date(computedAt).toLocaleString() }}
      </p>
      <p v-else class="text-xs text-n-slate-10">
        {{ t('SETTINGS.SPACES.VENUES.BUZZ.NO_SCORE') }}
      </p>

      <div v-if="categoryRows.some(row => row.value != null)" class="space-y-2">
        <p class="text-xs font-medium text-n-slate-11">
          {{ t('SETTINGS.SPACES.VENUES.BUZZ.BREAKDOWN') }}
        </p>
        <div
          v-for="row in categoryRows"
          :key="row.key"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-n-slate-11">{{ row.label }}</span>
          <span class="font-medium text-n-slate-12">{{
            row.value ?? '—'
          }}</span>
        </div>
      </div>

      <div v-if="history.length" class="space-y-2">
        <p class="text-xs font-medium text-n-slate-11">
          {{ t('SETTINGS.SPACES.VENUES.BUZZ.HISTORY') }}
        </p>
        <div class="max-h-32 overflow-y-auto space-y-1">
          <div
            v-for="(point, index) in history.slice(-7)"
            :key="`${point.scored_at}-${index}`"
            class="flex items-center justify-between text-xs text-n-slate-11"
          >
            <span>{{ new Date(point.scored_at).toLocaleDateString() }}</span>
            <span class="font-medium text-n-slate-12">{{ point.b }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
